import React, { useState } from "react";
import { cancelSubscriptionType, ComponentsTypeEnum } from "@/constants";
import SubmitForm from "../SubmitForm";
import { Result, Rate, Button, Flex } from "antd";
import { EditRateCustomerService } from "@/services";
import { getAgentFromScript } from "@/utils";
import toast from "react-hot-toast";
import { useI18n } from "@/contexts/I18nContext";
import CustomForm from "../CustomForm";
import styles from "../ConversationContent/index.module.scss";

interface ComponentRendererProps {
  message: any;
  setDialogueMessages: any;
  handleSendMessage: (values: any) => void;
}

/**
 * 消息组件渲染
 * @param message 当前对话消息
 * @param setDialogueMessages 设置对话消息函数
 * @param handleSendMessage 发送信息函数
 * @returns
 */
const ComponentRenderer: React.FC<ComponentRendererProps> = ({
  message,
  setDialogueMessages,
  handleSendMessage,
}) => {
  const { formatMessage } = useI18n();
  const [ticketUrl, setTicketUrl] = useState("");

  // 渲染提交表单
  const renderTicketForm = () => {
    if (
      message?.component === ComponentsTypeEnum.TICKET &&
      !message.hasTickFormSuccess
    ) {
      return (
        <li
          className={`${styles.dialogueItem} ${styles.bgStyle} ${styles.formContent}`}
          key={`form-${message.id}`}
        >
          <SubmitForm
            setDialogueMessages={setDialogueMessages}
            setTicketUrl={setTicketUrl}
          />
        </li>
      );
    }
    return null;
  };

  // 渲染提交表单成功后的内容
  const renderFormSuccess = () => {
    if (message.hasTickFormSuccess) {
      const thankText = formatMessage("thankText");
      const parts = thankText.split("{link}");
      return (
        <Result
          status="success"
          title=""
          subTitle={
            <>
              {parts[0]}
              <a
                href={ticketUrl || "#"}
                target="_black"
                onClick={
                  !ticketUrl
                    ? (e) => {
                        e.preventDefault();
                      }
                    : undefined
                }
              >
                {formatMessage("clickHere")}
              </a>
              {parts[1]}
            </>
          }
          extra={null}
          className={`${styles.dialogueItem} ${styles.bgStyle} ${styles.submitSuccessContent}`}
          key={`form-success-${message.id}`}
        />
      );
    }
    return null;
  };

  // 渲染评分组件
  const renderGradeComponent = () => {
    if (message?.component === ComponentsTypeEnum.GRADE) {
      return (
        <li
          className={`${styles.dialogueItem} ${styles.bgStyle} ${styles.evaluationContent}`}
          key={`rate-${message.id}`}
        >
          <span>{formatMessage("evaluationText")}</span>
          <Rate
            style={{ marginTop: 8 }}
            onChange={async (value) => {
              const res: any = await EditRateCustomerService({
                messageId: message.id,
                score: value,
                agent: getAgentFromScript(),
              });
              if (res.code === 0) {
                toast.success(formatMessage("thankEvaluation"));
              }
            }}
          />
        </li>
      );
    }
    return null;
  };

  // 渲染表单组件（邮箱、验证码等）
  const renderFormComponent = (
    componentType: ComponentsTypeEnum,
    formType: string,
  ) => {
    if (message.component === componentType) {
      return (
        <li
          className={`${styles.dialogueItem} ${styles.bgStyle} ${styles.formContent}`}
          key={`form-${formType}-${message.id}`}
        >
          <CustomForm
            type={componentType}
            handleSendMessage={handleSendMessage}
          />
        </li>
      );
    }
    return null;
  };

  // 渲染带选项的表单组件（产品列表、暂停选项等）
  const renderSelectFormComponent = (
    componentType: ComponentsTypeEnum,
    formType: string,
  ) => {
    if (message.component === componentType) {
      return (
        <li
          className={`${styles.dialogueItem} ${styles.bgStyle} ${styles.formContent}`}
          key={`form-${formType}-${message.id}`}
        >
          <CustomForm
            type={componentType}
            curMessage={message}
            selectOptions={message?.[componentType] || []}
            handleSendMessage={handleSendMessage}
          />
        </li>
      );
    }
    return null;
  };

  // 渲染取消订阅相关按钮组
  const renderCancelSubscriptionButtonGroup = (
    componentType: ComponentsTypeEnum,
    cancelType: any,
    key: string,
  ) => {
    if (message.component === componentType) {
      return (
        <li
          className={`${styles.dialogueItem} ${styles.formContent}`}
          style={{ maxWidth: "92%" }}
          key={`form-${key}-${message.id}`}
        >
          <Flex vertical align="center" justify="center" gap={8}>
            <Button
              type="primary"
              block
              shape="round"
              onClick={() => {
                handleSendMessage({
                  question: formatMessage("supportContact"),
                  [key]: cancelType.supportContact,
                });
              }}
            >
              {formatMessage("supportContact")}
            </Button>
            <Button
              type="default"
              block
              shape="round"
              onClick={() => {
                handleSendMessage({
                  question: formatMessage("stillCancel"),
                  [key]: cancelType.stillCancel,
                });
              }}
            >
              {formatMessage("stillCancel")}
            </Button>
          </Flex>
        </li>
      );
    }
    return null;
  };

  // 渲染折扣选择按钮组
  const renderDiscountSelection = () => {
    if (message.component === ComponentsTypeEnum.CANCEL_SUBSCRIPTION_DISCOUNT) {
      return (
        <li
          className={`${styles.dialogueItem} ${styles.formContent}`}
          key={`form-discount-selection-${message.id}`}
        >
          <Flex vertical align="center" justify="center" gap={8}>
            <Button
              type="primary"
              block
              shape="round"
              onClick={() => {
                handleSendMessage({
                  question: formatMessage("useDiscount"),
                  cancel_subscription_discount:
                    cancelSubscriptionType.useDiscount,
                });
              }}
            >
              {formatMessage("useDiscount")}
            </Button>
            <Button
              type="default"
              block
              shape="round"
              onClick={() => {
                handleSendMessage({
                  question: formatMessage("stillCancel"),
                  cancel_subscription_discount:
                    cancelSubscriptionType.stillCancel,
                });
              }}
            >
              {formatMessage("stillCancel")}
            </Button>
          </Flex>
        </li>
      );
    }
    return null;
  };

  // 渲染常见问题快捷标签
  const renderFaqTags = () => {
    if (
      message?.component === ComponentsTypeEnum.FAQ &&
      Array.isArray(message?.faq) &&
      message.faq.length > 0
    ) {
      return (
        <li
          className={`${styles.dialogueItem} ${styles.faqContent}`}
          key={`faq-${message.id}`}
        >
          <div className={styles.faqTagContainer}>
            {message.faq.map((item: any, index: number) => {
              if (!item) return null;
              return (
                <button
                  type="button"
                  className={styles.faqTag}
                  key={`faq-tag-${message.id}-${item?.faq_id ?? index}`}
                  onClick={() => {
                    handleSendMessage({
                      question: item?.question,
                      faq_id: item?.faq_id,
                      refer_id: item?.refer_id,
                    });
                  }}
                >
                  {item?.question || ""}
                </button>
              );
            })}
          </div>
        </li>
      );
    }
    return null;
  };

  return (
    <>
      {/* 渲染提交表单 */}
      {renderTicketForm()}

      {/* 提交表单成功后显示 */}
      {renderFormSuccess()}

      {/* 评分组件 */}
      {renderGradeComponent()}

      {/* 邮箱表单 */}
      {renderFormComponent(ComponentsTypeEnum.EMAIL, "email")}

      {/* 验证码表单 */}
      {renderFormComponent(ComponentsTypeEnum.CODE, "code")}

      {/* 取消订阅按钮组 */}
      {renderCancelSubscriptionButtonGroup(
        ComponentsTypeEnum.CANCEL_SUBSCRIPTION_BTN_OPTION,
        cancelSubscriptionType,
        "cancel_subscription_btn_option",
      )}

      {/* 取消订阅产品选择 */}
      {renderSelectFormComponent(
        ComponentsTypeEnum.CANCEL_SUBSCRIPTION_PRODUCT_LIST,
        "product-subscription",
      )}

      {/* 订阅暂停 */}
      {renderSelectFormComponent(
        ComponentsTypeEnum.CANCEL_SUBSCRIPTION_PAUSE,
        "subscription-pause",
      )}

      {/* 折扣选择 */}
      {renderDiscountSelection()}

      {/* 常见问题快捷标签 */}
      {renderFaqTags()}
    </>
  );
};

export default ComponentRenderer;
